// Data source: Semrush US database snapshot, October 2026.
// "traffic" values are Semrush ESTIMATED monthly organic visits — not first-party Analytics sessions.
// analytics: reserved structure for first-party GA4 / Search Console figures (populate later).

export const SNAPSHOT_LABEL = "Semrush US snapshot · October 2026";

export const ANALYTICS_TEMPLATE = {
  launchDate: null,
  daysSinceLaunch: null,
  organicUsers: null,
  organicSessions: null,
  searchImpressions: null,
  searchClicks: null,
  avgSearchPosition: null,
  conversions: null,
  momGrowth: null,
  launchToCurrentGrowth: null,
};

export const INDUSTRY_FILTERS = [
  { key: "all", label: "All" },
  { key: "legal", label: "Legal" },
  { key: "real-estate", label: "Real estate" },
  { key: "health-wellness", label: "Health and wellness" },
  { key: "fitness-sports", label: "Fitness" },
  { key: "home-services", label: "Home services" },
  { key: "b2b", label: "B2B" },
];

export const BUILD_FILTERS = [
  { key: "all", label: "All builds" },
  { key: "new-build", label: "New Build" },
  { key: "relaunch", label: "Relaunch" },
];

export const FEATURED_CASE_STUDIES = [
  {
    slug: "ninjathlete",
    domain: "Ninjathlete.com",
    url: "https://ninjathlete.com",
    caseStudyLink: "/case-studies/ninja-athlete-llm-visibility-fitness",
    screenshot: "/images/case-studies/ninjathlete.webp",
    category: "Sports Media",
    filterCategory: "fitness-sports",
    buildType: "Relaunch",
    description:
      "A content-first authority build spanning training, performance, athlete education, and sports discovery. Rebuilt on Emergent, March 2026.",
    metrics: [
      { label: "Ranking keywords", value: "3,332" },
      { label: "Est. monthly organic visits", value: "~1,273" },
      { label: "Top-10 positions", value: "321" },
    ],
    services: ["Content strategy", "Technical SEO", "AEO", "GEO", "Schema", "Internal linking"],
    details:
      "Ninjathlete was architected as a topical authority hub: question-led article structures across training, peptides, performance, and recovery; Article and FAQ schema on every guide; and dense internal linking between clusters. Rebuilt on Emergent in March 2026, the site has grown to 3,332 ranking keywords with 321 top-10 positions (Semrush US, Oct 2026).",
    analytics: { ...ANALYTICS_TEMPLATE },
  },
  {
    slug: "roblesfamilylaw",
    domain: "RoblesFamilyLaw.com",
    url: "https://roblesfamilylaw.com",
    screenshot: "/images/case-studies/roblesfamilylaw.webp",
    category: "Family Law",
    filterCategory: "legal",
    buildType: "New Build",
    description:
      "A high-trust local service website organized around real client questions, legal search intent, and clear service pathways.",
    metrics: [
      { label: "Ranking keywords", value: "993" },
      { label: "Est. monthly organic visits", value: "754" },
      { label: "Top-10 positions", value: "29" },
      { label: "AI Overview SERPs", value: "663" },
    ],
    services: ["Local SEO", "Service pages", "AEO", "Schema", "Content strategy", "Conversion optimization"],
    details:
      "Every practice area page answers the questions clients actually ask before hiring a family law attorney — in English and Spanish search intent. Attorney entity signals, LocalBusiness and FAQ schema, and prominent consultation pathways turn legal research traffic into booked consultations, with 663 AI Overview SERP appearances.",
    analytics: { ...ANALYTICS_TEMPLATE },
  },
  {
    slug: "dallasluxuryrealty",
    domain: "DallasLuxuryRealty.com",
    url: "https://dallasluxuryrealty.com",
    caseStudyLink: "/case-studies/dallas-luxury-realty-aeo-real-estate",
    screenshot: "/images/case-studies/dallasluxuryrealty.webp",
    category: "Luxury Real Estate",
    filterCategory: "real-estate",
    buildType: "Relaunch",
    description:
      "A competitive local property platform structured around neighborhoods, property intent, market expertise, and conversion.",
    metrics: [
      { label: "Ranking keywords", value: "475" },
      { label: "Est. monthly organic visits", value: "603" },
      { label: "Top-10 positions", value: "11" },
      { label: "AI Overview SERPs", value: "29" },
    ],
    services: ["Local SEO", "Technical SEO", "Location pages", "Content strategy", "Schema", "Conversion optimization"],
    details:
      "Luxury real estate is one of the most contested local SERP landscapes in Texas. The relaunch preserved existing equity through full redirect mapping, then rebuilt the site around neighborhood and property-intent pages with market-expertise content — keeping the brand visible for high-value Dallas luxury queries.",
    analytics: { ...ANALYTICS_TEMPLATE },
  },
  {
    slug: "trainagents",
    domain: "TrainAgents.com",
    url: "https://trainagents.com",
    caseStudyLink: "/case-studies/train-agents-ai-training-platform-aeo",
    screenshot: "/images/case-studies/trainagents.webp",
    category: "Real Estate Education",
    filterCategory: "real-estate",
    buildType: "Relaunch",
    description:
      "A topical education hub built to transform industry expertise into discoverable, answer-ready content.",
    metrics: [
      { label: "Ranking keywords", value: "570" },
      { label: "Est. monthly organic visits", value: "448" },
      { label: "Top-10 positions", value: "24" },
      { label: "AI Overview SERPs", value: "388" },
    ],
    services: ["Content strategy", "AEO", "GEO", "Schema", "Internal linking", "Technical SEO"],
    details:
      "Decades of real estate licensing expertise were restructured into state-specific course pathways and answer-ready licensing guides. Clear definitions, direct answers, and Course-aligned structured data give the brand 388 AI Overview SERP appearances in a niche where students increasingly research through AI assistants.",
    analytics: { ...ANALYTICS_TEMPLATE },
  },
  {
    slug: "garciawindsor",
    domain: "GarciaWindsor.com",
    url: "https://garciawindsor.com",
    screenshot: "/images/case-studies/garciawindsor.webp",
    category: "Legal Services",
    filterCategory: "legal",
    buildType: "Relaunch",
    description:
      "A local legal presence with extensive service coverage, strong entity signals, and a substantial AI-search footprint.",
    metrics: [
      { label: "Ranking keywords", value: "1,095" },
      { label: "Est. monthly organic visits", value: "353" },
      { label: "Top-10 positions", value: "38" },
      { label: "AI Overview SERPs", value: "639" },
    ],
    services: ["Local SEO", "Service pages", "Location pages", "Schema", "AEO", "GEO"],
    details:
      "Broad practice-area coverage paired with disciplined entity work: consistent attorney and firm signals, LocalBusiness and FAQ schema, and service pages mapped to how people actually search for legal help. The result is 1,095 ranking keywords and one of the portfolio's largest AI Overview footprints at 639 SERPs.",
    analytics: { ...ANALYTICS_TEMPLATE },
  },
  {
    slug: "biminihydrotherapy",
    domain: "BiminiHydrotherapy.com",
    url: "https://biminihydrotherapy.com",
    caseStudyLink: "/case-studies/bimini-hydrotherapy-aeo-geo-visibility",
    screenshot: "/images/case-studies/biminihydrotherapy.webp",
    category: "Health and Wellness",
    filterCategory: "health-wellness",
    buildType: "New Build",
    description:
      "A focused wellness website generating meaningful visibility from a compact, high-intent keyword footprint.",
    metrics: [
      { label: "Ranking keywords", value: "49" },
      { label: "Est. monthly organic visits", value: "241" },
      { label: "Top-10 positions", value: "9" },
      { label: "AI Overview SERPs", value: "16" },
    ],
    services: ["Local SEO", "Technical SEO", "Schema", "Content strategy", "Conversion optimization"],
    details:
      "Proof that footprint size isn't everything: 49 carefully targeted keywords drive an estimated 241 monthly organic visits because nearly every term carries buying intent. A tight service architecture, fast rendering, and accurate LocalBusiness schema let a compact site punch far above its weight.",
    analytics: { ...ANALYTICS_TEMPLATE },
  },
];

export const PORTFOLIO_SITES = [
  {
    slug: "dfwapartmentlocators",
    domain: "DFWApartmentLocators.com",
    url: "https://dfwapartmentlocators.com",
    caseStudyLink: "/case-studies/dfw-apartments-ai-search-real-estate",
    screenshot: "/images/case-studies/dfwapartmentlocators.webp",
    industry: "Real Estate",
    filterCategory: "real-estate",
    buildType: "Relaunch",
    keywords: "320",
    traffic: "143",
    localPack: "220",
    analytics: { ...ANALYTICS_TEMPLATE },
  },
  {
    slug: "graycontractingdfw",
    domain: "GrayContractingDFW.com",
    url: "https://graycontractingdfw.com",
    caseStudyLink: "/case-studies/gray-contracting-dfw-contractor-aeo",
    screenshot: "/images/case-studies/graycontractingdfw.webp",
    industry: "Home Services",
    filterCategory: "home-services",
    buildType: "New Build",
    keywords: "86",
    traffic: "22",
    localPack: "81",
    analytics: { ...ANALYTICS_TEMPLATE },
  },
  {
    slug: "dfwfitmeals",
    domain: "DFWFitMeals.com",
    url: "https://dfwfitmeals.com",
    screenshot: "/images/case-studies/dfwfitmeals.webp",
    industry: "Health and Wellness",
    filterCategory: "health-wellness",
    buildType: "New Build",
    keywords: "111",
    traffic: "5",
    localPack: "67",
    analytics: { ...ANALYTICS_TEMPLATE },
  },
  {
    slug: "usainkprinting",
    domain: "USAInkPrinting.com",
    url: "https://usainkprinting.com",
    caseStudyLink: "/case-studies/usa-ink-printing-aeo-ecommerce",
    screenshot: "/images/case-studies/usainkprinting.webp",
    industry: "B2B Printing",
    filterCategory: "b2b",
    buildType: "Relaunch",
    keywords: "106",
    traffic: null,
    localPack: "23",
    analytics: { ...ANALYTICS_TEMPLATE },
  },
  {
    slug: "goldenoakodessa",
    domain: "GoldenOakOdessa.com",
    url: "https://goldenoakodessa.com",
    screenshot: "/images/case-studies/goldenoakodessa.webp",
    industry: "Event Venue",
    filterCategory: null,
    buildType: "Relaunch",
    keywords: "75",
    traffic: null,
    localPack: "44",
    caseStudyLink: "/case-studies/golden-oak-venue-seo-aeo-geo-audit",
    analytics: { ...ANALYTICS_TEMPLATE },
  },
  {
    slug: "dallashaulstars",
    domain: "DallasHaulStars.com",
    url: "https://dallashaulstars.com",
    caseStudyLink: "/case-studies/dallas-haul-stars-junk-removal-aeo",
    screenshot: "/images/case-studies/dallashaulstars.webp",
    industry: "Home Services",
    filterCategory: "home-services",
    buildType: "New Build",
    keywords: "14",
    traffic: null,
    localPack: "10",
    analytics: { ...ANALYTICS_TEMPLATE },
  },
  {
    slug: "odessapilates",
    domain: "OdessaPilates.com",
    url: "https://odessapilates.com",
    screenshot: "/images/case-studies/odessapilates.webp",
    industry: "Fitness and Sports",
    filterCategory: "fitness-sports",
    buildType: "New Build",
    keywords: "10",
    traffic: null,
    localPack: "7",
    analytics: { ...ANALYTICS_TEMPLATE },
  },
  {
    slug: "odessasteel",
    domain: "OdessaSteel.com",
    url: "https://odessasteel.com",
    screenshot: "/images/case-studies/odessasteel.webp",
    industry: "B2B Industrial",
    filterCategory: "b2b",
    buildType: "New Build",
    keywords: "15",
    traffic: null,
    localPack: "7",
    analytics: { ...ANALYTICS_TEMPLATE },
  },
  {
    slug: "digitalmedianinja",
    domain: "DigitalMediaNinja.com",
    url: "https://digitalmedianinja.com",
    screenshot: "/images/case-studies/digitalmedianinja.webp",
    industry: "B2B Marketing",
    filterCategory: "b2b",
    buildType: "Relaunch",
    keywords: "1,206",
    traffic: "23",
    localPack: "47",
    analytics: { ...ANALYTICS_TEMPLATE },
  },
  {
    slug: "ninjarunclub",
    domain: "NinjaRunClub.com",
    url: "https://ninjarunclub.com",
    screenshot: "/images/case-studies/ninjarunclub.webp",
    industry: "Fitness and Sports",
    filterCategory: "fitness-sports",
    buildType: "New Build",
    keywords: null,
    traffic: null,
    localPack: null,
    analytics: { ...ANALYTICS_TEMPLATE },
  },
  {
    slug: "ninjathleticlub",
    domain: "NinjAthletiClub.com",
    url: "https://ninjathleticlub.com",
    screenshot: "/images/case-studies/ninjathleticlub.webp",
    industry: "Fitness and Sports",
    filterCategory: "fitness-sports",
    buildType: "New Build",
    keywords: null,
    traffic: null,
    localPack: null,
    analytics: { ...ANALYTICS_TEMPLATE },
  },
];

export const BUILD_SYSTEM = [
  {
    num: "01",
    title: "Search Architecture",
    copy: "We map services, locations, entities, audience questions, and search intent before a single page is built — so the site structure mirrors how your market actually searches.",
  },
  {
    num: "02",
    title: "Technical Foundation",
    copy: "Fast rendering, crawlable navigation, canonical URLs, XML sitemaps, clean redirects, tight indexation control, and mobile-first performance on every build.",
  },
  {
    num: "03",
    title: "Entity & Schema Layer",
    copy: "Accurate structured data so search engines and AI systems understand exactly who you are and what you offer — never schema types the content can't support.",
    tags: ["Organization", "LocalBusiness", "ProfessionalService", "Service", "Article", "Person", "FAQPage", "BreadcrumbList", "WebSite", "WebPage"],
  },
  {
    num: "04",
    title: "Answer-Ready Content",
    copy: "Clear definitions, direct answers, supporting evidence, expert context, headings, lists, tables, and FAQs — information architecture built to be quoted by answer engines.",
  },
  {
    num: "05",
    title: "Local Relevance",
    copy: "Service-area content, market-specific pages, internal linking, review signals, consistent business information, and clear conversion pathways for every location you serve.",
  },
  {
    num: "06",
    title: "Measurement & Iteration",
    copy: "Google Analytics, Search Console, Semrush, rankings, conversions, and content-gap analysis guide continuous improvement — not guesswork.",
  },
];

export const FAQ_ITEMS = [
  {
    q: "What is GEO?",
    a: "GEO (Generative Engine Optimization) is the practice of structuring a website so AI systems like ChatGPT, Gemini, Perplexity, and Google's AI Overviews can retrieve, understand, and cite it when generating answers. It focuses on entity clarity, structured data, direct answers, and content formats that language models can parse reliably.",
  },
  {
    q: "How is GEO different from traditional SEO?",
    a: "Traditional SEO optimizes for ranked lists of links in search results. GEO optimizes for being pulled into AI-generated answers. The two overlap heavily — clean technical foundations, authority, and structured content help both — but GEO adds emphasis on answer-ready formatting, schema markup, quotable definitions, and consistent entity signals across the web.",
  },
  {
    q: "What is AEO?",
    a: "AEO (Answer Engine Optimization) is optimizing content to win direct-answer placements: featured snippets, People Also Ask boxes, voice assistant results, and AI Overview citations. It relies on question-based headings, concise answers placed immediately after them, FAQ schema, and scannable page structure.",
  },
  {
    q: "Do you build new sites and relaunch existing sites?",
    a: "Both. This portfolio includes brand-new builds launched from scratch and strategic relaunches of existing websites. New builds are architected for search from day one; relaunches preserve existing equity while fixing the structural, technical, and content issues that hold visibility back.",
  },
  {
    q: "How quickly can a new website gain visibility?",
    a: "It varies with competition, market, and content depth. Focused local sites in this portfolio began registering local-pack and keyword visibility within their first months, while broader authority builds compound over several months of publishing. We set expectations per project rather than promising a fixed timeline.",
  },
  {
    q: "What schema markup do you implement?",
    a: "We implement the structured data types that accurately describe each business and page — typically Organization, LocalBusiness, ProfessionalService, Service, Article, Person, FAQPage, BreadcrumbList, WebSite, and WebPage. We only mark up content that actually exists on the page, keeping every site eligible for rich results without risking manual actions.",
  },
  {
    q: "Can you preserve rankings during a website relaunch?",
    a: "Yes. Relaunches follow a preservation protocol: full URL inventory, 301 redirect mapping, content parity checks, canonical and metadata continuity, and post-launch monitoring in Search Console. Some short-term fluctuation is normal, but the process is designed to retain — and then grow — existing rankings.",
  },
  {
    q: "Are rankings or traffic guaranteed?",
    a: "No — and you should be skeptical of anyone who guarantees them. Search results depend on competition, algorithm changes, and AI systems outside any agency's control. What we commit to is a measurable process: sound architecture, accurate structured data, answer-ready content, and transparent reporting from Analytics and Search Console.",
  },
];

export const MEASUREMENT_DISCLOSURE =
  "Snapshot metrics shown on this page were retrieved from the Semrush US database in October 2026. \u201CTraffic\u201D represents Semrush\u2019s estimated monthly organic traffic and should not be interpreted as first-party Analytics sessions. Search visibility and rankings change over time, and third-party estimates may differ from Google Analytics and Google Search Console.";
